import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectActiveMatter } from '@/matter/store/selectors'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { useCreateNewChat } from '@/chat-common/new_chat/hooks/useCreateNewChat'
import { matterPaths } from '@/routing/paths'
import { AuthContext } from '@/context/auth-context'
import { MatterJurisdictionEntrySource } from '@/analytics/enums/matter-jurisdiction.enums'

export function useMatterOverview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const matter = useAppSelector(selectActiveMatter)
  const { createNewChat } = useCreateNewChat()
  const { userAccountData } = useContext(AuthContext)

  // source tags which surface opened the dialog, for attribution (defaults to unset, i.e. a direct edit).
  const handleEditMatter = useCallback(
    (source?: MatterJurisdictionEntrySource) => {
      if (!matter) return
      dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Edit, matterId: matter.id, source }))
    },
    [dispatch, matter]
  )

  const handleEditMatterJurisdiction = useCallback(() => handleEditMatter(MatterJurisdictionEntrySource.MatterDetailCta), [handleEditMatter])

  const handleMatterSettings = useCallback(() => {
    if (!matter) return
    navigate(matterPaths.settings(matter.id))
  }, [navigate, matter])

  const handleDeleteMatter = useCallback(() => {
    if (!matter) return
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Delete, matterId: matter.id }))
  }, [dispatch, matter])

  const handleNewChat = useCallback(() => {
    if (!matter) return
    createNewChat(true, matter.id)
  }, [createNewChat, matter])

  const handleNavigateToAccount = useCallback(() => navigate('/dashboard/account'), [navigate])

  return {
    matter,
    handleEditMatter,
    handleEditMatterJurisdiction,
    handleMatterSettings,
    handleDeleteMatter,
    handleNewChat,
    handleNavigateToAccount,
    showCollaborationUpsell: !userAccountData?.isOrgAccount,
  }
}
